// src/modules/app-views/omnichannel/pages/Init/components/ConversationBody.jsx
// *******************************************************************************
// CONVERSATION BODY
// *******************************************************************************

import { Alert, Avatar, Badge, Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Scrollbars } from 'react-custom-scrollbars-2';
import ContextFlagDivider from './ContextFlagDivider';
import MessageBubble from './MessageBubble';
import appService from 'services/appService';
import translateX from 'utils/translateX';

// *******************************************************************************
// COMPONENT
// *******************************************************************************

const ConversationBody = ({
  // Refs
  chatBodyRef,
  // Data
  messages,
  conversationId,
  isGroup,
  groupMembers,
  agentAvatar,
  dayFirstIndices,
  contextChangeMap,
  // Translation state
  translations,
  translatingIndex,
  autoTranslatingIndices,
  outboundOriginals,
  outboundDeliveryLangs,
  outboundTranslateErrors,
  // Handlers
  onTranslateMessage,
  onReaction,
  // Channel state
  isChannelRemoved,
  isChannelDisconnected,
  // Closure info
  closedContext,
  // Theme
  isDark,
  bodyBg,
  tailStyles,
  tailShadow,
  token,
}) => {
  const isMobile = appService.isMobile();

  return (
    <div
      className="chat-content-body"
      style={{
        ...bodyBg,
        backgroundRepeat: 'repeat',
        backgroundSize: '400px',
        padding: 0,
      }}
    >
      <Scrollbars ref={chatBodyRef} autoHide>
        <style>{tailStyles}</style>
        <div
          style={{
            paddingRight: isMobile ? 8 : isGroup ? 20 : 48,
            paddingLeft: isMobile ? 8 : isGroup ? 20 : 48,
          }}
        >
          {messages.map((elm, i) => {
            const prevMsg = messages[i - 1];
            // In group chats, a new "group" starts when the sender changes (even if both are inbound)
            const isFirstInGroup =
              i === 0 ||
              prevMsg?.direction !== elm.direction ||
              (isGroup &&
                elm.direction === 'inbound' &&
                prevMsg?.sender?.name !== elm.sender?.name);
            const tailClass = isFirstInGroup
              ? elm.direction === 'inbound'
                ? 'bubble-tail-left'
                : 'bubble-tail-right'
              : '';

            // Show date separator only at the first message of each calendar day
            const showDateSep = dayFirstIndices.has(i);

            return (
              <div key={`msg-${conversationId}-${i}`}>
                {showDateSep && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      margin: '12px 0 24px',
                    }}
                  >
                    <span
                      style={{
                        backgroundColor: isDark ? 'rgba(11,20,26,0.7)' : 'rgba(229,221,213,0.92)',
                        color: isDark ? '#e9edef' : '#54656f',
                        padding: '5px 12px 4px',
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: 1,
                        boxShadow: isDark
                          ? '0 1px 2px rgba(0,0,0,0.3)'
                          : '0 1px 2px rgba(11,20,26,0.1)',
                      }}
                    >
                      {formatDateSeparator(elm.timestamp)}
                    </span>
                  </div>
                )}
                {contextChangeMap.has(i) && (
                  <ContextFlagDivider type={contextChangeMap.get(i)} isDark={isDark} />
                )}
                <div
                  key={`msg-inner-${conversationId}-${i}`}
                  className={`msg ${!elm.content?.text && !elm.content?.mediaType ? 'datetime' : ''} ${elm.direction === 'inbound' ? 'msg-recipient' : elm.direction === 'outbound' ? 'msg-sent' : ''}`}
                  style={
                    isGroup &&
                    isFirstInGroup &&
                    i > 0 &&
                    elm.direction === 'inbound' &&
                    prevMsg?.direction === 'inbound'
                      ? { marginTop: 12 }
                      : undefined
                  }
                >
                  {elm.direction === 'inbound' && (isGroup || elm.sender?.avatar) ? (
                    <div
                      style={{
                        marginRight: 6,
                        visibility: isFirstInGroup ? 'visible' : 'hidden',
                        flexShrink: 0,
                        alignSelf: 'flex-start',
                        marginTop: 2,
                      }}
                    >
                      <Avatar
                        src={
                          getMemberAvatar(elm.sender, groupMembers) ||
                          elm.sender?.avatar ||
                          undefined
                        }
                        size={isGroup ? 28 : 32}
                        style={
                          isGroup
                            ? { backgroundColor: getSenderColor(elm.sender?.name), fontSize: 12 }
                            : undefined
                        }
                      >
                        {getAvatarContent(elm.sender?.name || '?')}
                      </Avatar>
                    </div>
                  ) : null}

                  {elm.content?.text || elm.content?.mediaUrl || elm.content?.mediaType ? (
                    <MessageBubble
                      msg={elm}
                      msgIndex={i}
                      conversationId={conversationId}
                      isGroup={isGroup}
                      isFirstInGroup={isFirstInGroup}
                      translation={translations[i]}
                      translatingIndex={translatingIndex}
                      autoTranslatingIndices={autoTranslatingIndices}
                      outboundOriginal={outboundOriginals[i]}
                      outboundDeliveryLang={outboundDeliveryLangs[i]}
                      outboundTranslateError={outboundTranslateErrors[i]}
                      onTranslateMessage={onTranslateMessage}
                      onReaction={onReaction}
                      isDark={isDark}
                      tailShadow={tailShadow}
                      tailClass={tailClass}
                      token={token}
                    />
                  ) : null}

                  {/* Group: outbound avatar on the right */}
                  {isGroup && elm.direction === 'outbound' && (
                    <div
                      style={{
                        marginLeft: 4,
                        visibility: isFirstInGroup ? 'visible' : 'hidden',
                        flexShrink: 0,
                        alignSelf: 'flex-start',
                        marginTop: 2,
                      }}
                    >
                      <Avatar
                        src={agentAvatar || elm.sender?.avatar || undefined}
                        size={28}
                        style={{
                          backgroundColor: getSenderColor(elm.sender?.name || 'Agent'),
                          fontSize: 12,
                        }}
                      >
                        {getAvatarContent(elm.sender?.name || 'A')}
                      </Avatar>
                    </div>
                  )}

                  {!elm.content?.text &&
                  !elm.content?.mediaUrl &&
                  !elm.content?.mediaType &&
                  elm.timestamp ? (
                    <MessageBubble
                      msg={elm}
                      msgIndex={i}
                      conversationId={conversationId}
                      isGroup={isGroup}
                      isFirstInGroup={isFirstInGroup}
                      translation={translations[i]}
                      translatingIndex={translatingIndex}
                      autoTranslatingIndices={autoTranslatingIndices}
                      outboundOriginal={outboundOriginals[i]}
                      outboundDeliveryLang={outboundDeliveryLangs[i]}
                      outboundTranslateError={outboundTranslateErrors[i]}
                      onTranslateMessage={onTranslateMessage}
                      onReaction={onReaction}
                      isDark={isDark}
                      tailShadow={tailShadow}
                      tailClass={tailClass}
                      token={token}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}

          {/* Channel disconnected notice — Baileys instance exists but WhatsApp session is offline */}
          {isChannelDisconnected && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: 16,
              }}
            >
              <div style={{ maxWidth: isMobile ? '100%' : '70%', width: '100%' }}>
                <Alert
                  className="mb-5"
                  type="warning"
                  showIcon
                  title={translateX('channel_disconnected')}
                  description={translateX('channel_disconnected_warning')}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          )}

          {/* Channel removed notice — shown at the bottom when the source no longer exists */}
          {isChannelRemoved && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: 16,
                // padding: '16px 24px 24px'
              }}
            >
              <div style={{ maxWidth: isMobile ? '100%' : '70%', width: '100%' }}>
                <Alert
                  className="mb-5"
                  type="warning"
                  showIcon
                  title={translateX('channel_unavailable')}
                  description={translateX('channel_unavailable_warning')}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          )}

          {/* Closure summary — shown at the bottom when conversation is resolved */}
          {closedContext && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                // padding: '16px 24px 24px'
                paddingTop: 16,
              }}
            >
              <style>{`
                .omni-closure-ribbon .ant-ribbon {
                  margin-right: 20px;
                  margin-top: 0px !important;
                }
              `}</style>
              <div
                className="omni-closure-ribbon"
                style={{ maxWidth: isMobile ? '100%' : '70%', width: '100%' }}
              >
                <Badge.Ribbon
                  text={
                    closedContext.reason ? translateX(`close_reason_${closedContext.reason}`) : ''
                  }
                  color={getReasonRibbonColor(closedContext.reason)}
                >
                  <Alert
                    className="mb-5"
                    type="info"
                    showIcon
                    style={{ width: '100%' }}
                    description={
                      <div>
                        <div style={{ fontStyle: 'italic', marginBottom: 8, paddingRight: 80 }}>
                          &ldquo;{closedContext.description || '—'}&rdquo;
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                          }}
                        >
                          <span style={{ fontSize: 11, opacity: 0.7 }}>
                            {closedContext.closedAt &&
                              new Date(closedContext.closedAt).toLocaleString([], {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                          </span>
                          {closedContext.closedByName && (
                            <span style={{ fontSize: 11, fontWeight: 600 }}>
                              {closedContext.closedByName}
                            </span>
                          )}
                        </div>
                      </div>
                    }
                  />
                </Badge.Ribbon>
              </div>
            </div>
          )}
        </div>
      </Scrollbars>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************

export default ConversationBody;

// *******************************************************************************
// HELPERS
// *******************************************************************************

// Badge.Ribbon color for closure reason (accepts preset names)
// Returns a letter initial or <UserOutlined /> if the first char is not alphanumeric
function getAvatarContent(name) {
  const ch = (name || '?').charAt(0).toUpperCase();
  return /[A-Za-z0-9]/.test(ch) ? ch : <UserOutlined />;
}

function getReasonRibbonColor(reason) {
  switch (reason) {
    case 'resolved':
      return 'green';
    case 'doubt':
      return 'purple';
    case 'mistake':
      return 'magenta';
    case 'no_response':
      return 'gold';
    case 'other':
      return 'geekblue';
    default:
      return 'blue';
  }
}

function getReasonTagColor(reason) {
  switch (reason) {
    case 'resolved':
      return 'green';
    case 'doubt':
      return 'purple';
    case 'mistake':
      return 'magenta';
    case 'no_response':
      return 'gold';
    case 'other':
      return 'geekblue';
    default:
      return 'default';
  }
}

function formatDateSeparator(timestamp) {
  if (!timestamp) return '';
  const msgDate = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (isSameDay(msgDate, today)) return 'Hoje';
  if (isSameDay(msgDate, yesterday)) return 'Ontem';

  const diffMs = today.setHours(0, 0, 0, 0) - msgDate.setHours(0, 0, 0, 0);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays <= 7) {
    return msgDate.toLocaleDateString([], { weekday: 'long' });
  }

  return new Date(timestamp).toLocaleDateString([], {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// Find a group member's avatar by matching sender LID (primary) or name (fallback)
function getMemberAvatar(sender, members) {
  if (!sender || !members?.length) return null;
  // Primary: match by LID (stored in sender.id for group messages)
  if (sender.id) {
    const byLid = members.find(m => m.lid === sender.id);
    if (byLid?.avatar) return byLid.avatar;
  }
  // Fallback: match by name
  if (sender.name) {
    const byName = members.find(m => m.name && m.name === sender.name);
    if (byName?.avatar) return byName.avatar;
  }
  return null;
}

// WhatsApp-style sender colors for group chats (same palette as WhatsApp Web)
const SENDER_COLORS = [
  '#06cf9c',
  '#25d366',
  '#00a884',
  '#53bdeb',
  '#007bfc',
  '#ff6b6b',
  '#ffa726',
  '#ab47bc',
  '#ec407a',
  '#5c6bc0',
  '#26a69a',
  '#8d6e63',
  '#78909c',
  '#d4e157',
];

function getSenderColor(name) {
  if (!name) return SENDER_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return SENDER_COLORS[Math.abs(hash) % SENDER_COLORS.length];
}
